"use client";

import { GET_SHORTLISTED_PROJECTS_QUERY } from "@/gql/project";
import { useBuyerPropertiesStore } from "@/providers/global/stores/buyer-properties-store";
import { BuyerProject } from "@/types/buyerProject";
import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import { useRelationship } from "./use-relationship";

/**
 * Custom hook to fetch and manage shortlisted properties (both units and projects).
 * This hook wraps the query logic and store updates for shortlisted projects,
 * while buyer units are handled through the BUYER_DASHBOARD_QUERY.
 *
 * @param buyerGroupId - The buyer group ID for fetching shortlisted properties
 * @returns Query data and loading/error states
 */
export function useShortlistedProperties(buyerGroupId: string | undefined) {
	const { user, isSeller, isAuthenticated } = useRelationship();
	const { setShortlistedProjects } = useBuyerPropertiesStore();

	// Fetch shortlisted projects from resolver
	const {
		data: shortlistedProjectsData,
		loading,
		error,
	} = useQuery(GET_SHORTLISTED_PROJECTS_QUERY, {
		variables: {
			filters: {
				getShortlistedProjects: {
					buyerId: buyerGroupId || "",
					userId: user?.id || "",
				},
			},
		},
		skip: !isAuthenticated || !buyerGroupId || !user?.id || isSeller,
		fetchPolicy: "network-only", // Always fetch fresh data to ensure shortlist state is current
		nextFetchPolicy: "cache-first", // Use cache for subsequent fetches
		notifyOnNetworkStatusChange: true, // Ensure query re-runs when skip condition changes
		errorPolicy: "all",
		// onCompleted: data => {
		// 	logger.info("✨ [useShortlistedProperties] GET_SHORTLISTED_PROJECTS_QUERY completed:", {
		// 		hasData: !!data?.projects?.getShortlistedProjects,
		// 		projectCount: data?.projects?.getShortlistedProjects?.length || 0,
		// 		projectIds: data?.projects?.getShortlistedProjects?.map((p: { id: string }) => p.id) || [],
		// 		fullResponse: data?.projects?.getShortlistedProjects,
		// 		queryVariables: { buyerId: buyerGroupId, userId: user?.id },
		// 	});
		// },
		// onError: queryError => {
		// 	logger.error("❌ [useShortlistedProperties] GET_SHORTLISTED_PROJECTS_QUERY error:", queryError);
		// },
	});

	// Update store when shortlisted projects are fetched
	useEffect(() => {
		// logger.info("🔍 [useShortlistedProperties] Shortlisted Projects Data Changed:", {
		// 	hasData: !!shortlistedProjectsData?.projects?.getShortlistedProjects,
		// 	projectCount: shortlistedProjectsData?.projects?.getShortlistedProjects?.length || 0,
		// 	projectIds: shortlistedProjectsData?.projects?.getShortlistedProjects?.map((p: { id: string }) => p.id) || [],
		// 	rawData: shortlistedProjectsData?.projects?.getShortlistedProjects,
		// });

		if (shortlistedProjectsData?.projects?.getShortlistedProjects) {
			const projects = shortlistedProjectsData.projects.getShortlistedProjects.map(
				(p: { id: string; name: string; cover?: string; localityName?: string }) => ({
					id: p.id,
					project: { id: p.id, name: p.name },
					processDetails: [{ process: "shortlisting", stage: "done", status: "active" }],
				}),
			) as BuyerProject[];

			// logger.info("✅ [useShortlistedProperties] Setting shortlisted projects in store:", {
			// 	projectCount: projects.length,
			// 	projects: projects.map((p: BuyerProject) => ({ id: p.id, projectId: p.project?.id, name: p.project?.name })),
			// });

			// Update the store with shortlisted projects
			setShortlistedProjects(projects);
		} else {
			// logger.info("⚠️ [useShortlistedProperties] No shortlisted projects data to set");
		}
	}, [shortlistedProjectsData, setShortlistedProjects]);

	return {
		shortlistedProjectsData,
		loading,
		error,
	};
}
